
const libPictView = require('pict-view');
// Field key -> typed editor mapping plus the pure parse/serialize helpers (see the module for the rationale).
const libConfigFieldSchema = require('./EmailNotifications-ConfigFieldSchema.js');

const WEEKDAY_LABELS = [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ];

/** How many available-side rows a dual list puts in the DOM at once; the rest wait behind the filter. */
const AVAILABLE_RENDER_CAP = 250;

/**
 * EmailNotificationsEditor — the #/EmailRule/New and #/EmailRule/:ID editor.
 *
 * The core rule form (Enabled / Handler / Projects / DocumentType / Status) sits above a stable
 * #EmailNotifications-Dynamic region that renders, for the selected handler, the SCHEDULE editor (scheduled
 * handlers) and the custom-content CONFIG form.
 *
 * The config form is FORM-BASED end to end: every field the handler declares gets a per-key "Customize"
 * switch (off = the handler's built-in default applies and nothing is stored) and a typed editor chosen by
 * EmailNotifications-ConfigFieldSchema — recipient rows, `[field, value]` filter pairs, role checkboxes, tag
 * and address chips, routing rows, template inputs with a variable inserter, and plain code editors for the
 * message bodies. A raw JSON textarea survives only as the escape hatch for a shape the schema does not
 * model, and invalid JSON there blocks the save rather than silently storing a string.
 *
 * Save runs the full provider sequence (EmailRule/Upsert + double-save + Configuration/Put +
 * EmailRule/SetSchedule).
 *
 * The dynamic region is re-rendered on its own (never a full page re-render) so the code editors survive
 * base-field edits, and a single field can re-render in isolation (#emne-fld-<slug>) so adding a row does not
 * disturb the other fields' editors.
 */
class EmailNotificationsEditor extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		// Config field keys currently rendered as code editors (for capture + re-mount).
		this._activeBodyFields = [];
		// Mounted CodeMirror instances, keyed by config field key.
		this._bodyEditors = {};
		// Per-field validation errors from the last capture, keyed by config field key. Blocks save.
		this._configErrors = {};
		// Filter text per dual-list picker ('projects' / 'status'), one per pane.
		this._dualFilters = {};
		this._dualSelectedFilters = {};
	}

	get provider()
	{
		return this.pict.providers.EmailNotifications;
	}

	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	/** Escape a value that lands inside a single-quoted inline JS handler argument (config keys, from the server). */
	_attr(pValue)
	{
		return this._esc(String(pValue == null ? '' : pValue).replace(/\\/g, '\\\\').replace(/'/g, "\\'"));
	}

	onBeforeRender(pRenderable)
	{
		const tmpEditor = this.provider ? this.provider.editorState : { Rule: null, Loading: false, Error: '' };
		const tmpApp = this.pict.AppData.EmailNotifications || {};
		tmpApp.EditorLoadingSlot = tmpEditor.Loading ? [ {} ] : [];
		tmpApp.EditorErrorSlot = tmpEditor.Error ? [ { Message: tmpEditor.Error } ] : [];
		tmpApp.EditorTitle = tmpEditor.IsNew ? 'New Email Rule' : 'Edit Email Rule';
		tmpApp.EditorRuleSlot = (tmpEditor.Rule && !tmpEditor.Loading && !tmpEditor.Error) ? [ this._formRecord(tmpEditor.Rule, tmpEditor) ] : [];
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this._renderActions();
		// The base form just rendered; fill the handler description + the dynamic (schedule/config) region.
		if (this.provider.editorState.Rule)
		{
			// The whole form was just replaced — any previously mounted code editors are detached DOM now.
			this._destroyBodyEditors();
			this._activeBodyFields = [];
			this._renderHandlerDesc();
			this._renderDualLists();
			this._renderDynamic();
		}
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/** Base form render record (the fields that don't depend on the handler's config). */
	_formRecord(pRule, pEditor)
	{
		const tmpLookups = this.provider.state.Lookups || {};
		const tmpHandlers = this.provider.state.Handlers || {};
		return {
			Enabled: pRule.Enabled ? 'checked' : '',
			DisabledAttr: pEditor.Editable ? '' : 'disabled',
			HandlerOptions: Object.keys(tmpHandlers).map((pHandler) => ({ Value: pHandler, Label: this.provider.handlerLabel(pHandler), SelectedAttr: (pHandler === pRule.Handler) ? 'selected' : '' })),
			DocTypeOptions: [ { Hash: '', Name: 'Any document type', SelectedAttr: pRule.DocumentType ? '' : 'selected' } ]
				.concat((tmpLookups.DocTypes || []).map((pDoc) => ({ Hash: pDoc.Hash, Name: pDoc.Name, SelectedAttr: (pDoc.Hash === pRule.DocumentType) ? 'selected' : '' }))),
			ExistingSlot: (!pEditor.IsNew) ? [ { IDEmailRule: pRule.IDEmailRule } ] : [],
			ReadOnlySlot: (!pEditor.Editable) ? [ {} ] : [],
		};
	}

	// --- Base field capture / change ----------------------------------------------------------------

	_captureForm()
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule) { return; }
		const tmpGet = (pID) => document.getElementById(pID);
		if (tmpGet('emne-enabled')) { tmpRule.Enabled = tmpGet('emne-enabled').checked; }
		if (tmpGet('emne-handler')) { tmpRule.Handler = tmpGet('emne-handler').value; }
		if (tmpGet('emne-doctype')) { tmpRule.DocumentType = tmpGet('emne-doctype').value; }
		// Projects and Status are held by the dual-list pickers directly on the rule — reading them back out of
		// the DOM is what used to drop any selection that was not in the loaded option set.
	}

	onFieldChanged()
	{
		this._captureForm();
		this.provider._markRuleDirty();
		this._renderActions();
	}

	/** Handler changed: capture everything, then refresh the description + dynamic region for the new handler. */
	onHandlerChanged()
	{
		// Read the config region FIRST — it is still showing the previous handler's fields, and capturing it
		// after _captureForm() adopts the new handler would interpret the old controls with the new schema.
		this._captureDynamic();
		this._captureForm();
		this._pruneOverridesForHandler();
		this.provider._markRuleDirty();
		this._renderHandlerDesc();
		this._renderDynamic();
		this._renderActions();
	}

	/**
	 * After an email-type change, drop the overrides the new handler does not declare. Keys the handlers share
	 * (Subject, CC/BCC, the message bodies, FilterDocuments) carry over, which is the portable part of the
	 * admin's work; anything else would be dead weight in the new handler's config blob.
	 */
	_pruneOverridesForHandler()
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule || !tmpRule.ConfigValue) { return; }
		const tmpMeta = (this.provider.state.Handlers || {})[tmpRule.Handler] || {};
		const tmpDefaults = tmpMeta.defaultConfiguration || {};
		Object.keys(tmpRule.ConfigValue).forEach((pKey) =>
		{
			if (!Object.prototype.hasOwnProperty.call(tmpDefaults, pKey)) { delete tmpRule.ConfigValue[pKey]; }
		});
	}

	// --- Handler description + dynamic region --------------------------------------------------------

	_renderHandlerDesc()
	{
		if (!this.pict.ContentAssignment.getElement('#EmailNotifications-Handler-Desc')) { return; }
		const tmpRule = this.provider.editorState.Rule;
		const tmpMeta = (this.provider.state.Handlers || {})[tmpRule.Handler] || {};
		const tmpSched = tmpMeta.scheduled ? `<span class="emne-sched-note"><span class="icon is-small"><i class="fas fa-clock"></i></span> Scheduled</span>` : '';
		this.pict.ContentAssignment.assignContent('#EmailNotifications-Handler-Desc',
			`<div class="emne-handler-desc">${this._esc(tmpMeta.description || '')} ${tmpSched}</div>`);
	}

	/** Render the schedule + config sections for the current handler into the dynamic region. */
	_renderDynamic()
	{
		if (!this.pict.ContentAssignment.getElement('#EmailNotifications-Dynamic')) { return; }
		// Capture the region we're about to replace (marshals the code editors out to ConfigValue), then tear
		// those editors down — their DOM is about to be discarded and a stale instance would leak listeners.
		this._captureDynamic();
		this._destroyBodyEditors();
		const tmpEditor = this.provider.editorState;
		const tmpRule = tmpEditor.Rule;
		const tmpMeta = (this.provider.state.Handlers || {})[tmpRule.Handler] || {};
		let tmpHTML = '';
		if (tmpMeta.scheduled) { tmpHTML += this._scheduleHTML(tmpRule, tmpEditor); }
		tmpHTML += this._configHTML(tmpRule, tmpEditor);
		this.pict.ContentAssignment.assignContent('#EmailNotifications-Dynamic', tmpHTML);
		this._mountActiveBodyEditors();
	}

	// --- "Applies to" dual-list pickers -------------------------------------------------------------
	//
	// Projects and Status are side-by-side available/selected lists rather than multi-selects. Two reasons
	// beyond ergonomics: a customer can have thousands of projects (QA customer 10 has 3334), which no
	// multi-select handles usefully, and — more importantly — the SELECTED side is driven by the rule itself,
	// not by the loaded option set. A selection the source list does not contain stays selected and is shown
	// by id, instead of vanishing from the form and being silently dropped on the next save.
	//
	// The available side renders at most AVAILABLE_RENDER_CAP rows and narrows through the filter box, so the
	// DOM stays small no matter how many projects the customer has.

	/** The dual lists on the base form: id -> how to read/write the rule and where to render. */
	get dualLists()
	{
		return {
			projects:
			{
				Target: '#EmailNotifications-Projects',
				Label: 'projects',
				Numeric: true,
				Options: () => (this.provider.state.Lookups.Projects || []).map((pProject) => ({ Value: pProject.IDProject, Label: pProject.Name })),
				Read: () => (this.provider.editorState.Rule.Projects || []),
				Write: (pValues) => { this.provider.editorState.Rule.Projects = pValues; },
				LabelFor: (pValue) => this.provider.projectLabel(pValue),
				// Projects are server-side search (a customer can have thousands) — the Available pane queries the
				// server per keystroke instead of filtering an in-memory list. Statuses stays a client-side list.
				ServerSearch:
				{
					Search: (pTerm) => this.provider.searchProjectsAsync(pTerm),
					SearchAll: (pTerm) => this.provider.searchAllProjectsAsync(pTerm),
				},
			},
			status:
			{
				Target: '#EmailNotifications-Status',
				Label: 'statuses',
				Numeric: false,
				Options: () => (this.provider.state.Lookups.Statuses || []).map((pStatus) => ({ Value: pStatus, Label: pStatus })),
				Read: () => (this.provider.editorState.Rule.Status || []),
				Write: (pValues) => { this.provider.editorState.Rule.Status = pValues; },
				LabelFor: (pValue) => String(pValue),
			},
		};
	}

	/** Render both dual lists into the base form (called after the form template lands). */
	_renderDualLists()
	{
		const tmpLists = this.dualLists;
		Object.keys(tmpLists).forEach((pID) => this._renderDualList(pID));
		return tmpLists;
	}

	_renderDualList(pID)
	{
		const tmpSpec = this.dualLists[pID];
		if (!tmpSpec || !this.pict.ContentAssignment.getElement(tmpSpec.Target)) { return; }
		this.pict.ContentAssignment.assignContent(tmpSpec.Target, this._dualListHTML(pID, tmpSpec));
		this.pict.CSSMap.injectCSS();
	}

	_dualListHTML(pID, pSpec)
	{
		const tmpDisabled = this.provider.editorState.Editable ? '' : 'disabled';
		const tmpFilter = (this._dualFilters || {})[pID] || '';
		const tmpSelected = pSpec.Read();
		const tmpTotal = pSpec.Options().length;
		const tmpSelectedFilter = (this._dualSelectedFilters || {})[pID] || '';
		// Both moves act on what the neighbouring pane is currently SHOWING, so they stay predictable while a
		// filter is applied ("add all" after filtering to a district adds that district, not all 3334).
		const tmpControls = tmpDisabled ? '' : `
			<div class="emne-dualmid">
				<button type="button" class="button is-small is-light" title="Add everything the Available list is showing" onclick="_Pict.views.EmailNotificationsEditor.onDualAddAll('${pID}')">Add all &raquo;</button>
				<button type="button" class="button is-small is-light" title="Remove everything the Selected list is showing" onclick="_Pict.views.EmailNotificationsEditor.onDualClear('${pID}')">&laquo; Remove all</button>
			</div>`;
		// The two panes are deliberately identical in structure — same header, same filter row, same list — so
		// their tops and bottoms line up whatever is in them.
		return `
			<div class="emne-dual">
				<div class="emne-dualpane">
					<div class="emne-dualhead">Available <span class="emne-dualcount" id="emne-dualavailcount-${pID}">${this._dualAvailableMatches(pID, pSpec).length}</span></div>
					<input type="text" class="input is-small emne-dualfilter" id="emne-dualfilter-${pID}" value="${this._esc(tmpFilter)}" placeholder="${pSpec.ServerSearch ? `Search ${this._esc(pSpec.Label)}…` : `Filter ${this._esc(String(tmpTotal))} ${this._esc(pSpec.Label)}…`}" ${tmpDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDualFilter('${pID}', this.value)">
					<div class="emne-duallist" id="emne-dualavail-${pID}">${this._dualAvailableHTML(pID, pSpec)}</div>
				</div>
				${tmpControls}
				<div class="emne-dualpane">
					<div class="emne-dualhead">Selected <span class="emne-dualcount" id="emne-dualselcount-${pID}">${tmpSelected.length}</span></div>
					<input type="text" class="input is-small emne-dualfilter" id="emne-dualselfilter-${pID}" value="${this._esc(tmpSelectedFilter)}" placeholder="Filter selected…" ${tmpDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDualSelectedFilter('${pID}', this.value)">
					<div class="emne-duallist emne-duallist-selected" id="emne-dualsel-${pID}">${this._dualSelectedHTML(pID, pSpec)}</div>
				</div>
			</div>`;
	}

	/** The available side: everything not already selected, narrowed by the filter and capped for the DOM. */
	_dualAvailableHTML(pID, pSpec)
	{
		const tmpDisabled = this.provider.editorState.Editable ? '' : 'disabled';
		const tmpMatches = this._dualAvailableMatches(pID, pSpec);
		if (!tmpMatches.length)
		{
			const tmpFilter = (this._dualFilters || {})[pID] || '';
			if (pSpec.ServerSearch)
			{
				if ((this._dualSearching || {})[pID]) { return `<p class="emne-dualempty">Searching…</p>`; }
				if (!tmpFilter) { return `<p class="emne-dualempty">Type to search ${this._esc(pSpec.Label)}…</p>`; }
				return `<p class="emne-dualempty">Nothing matches that search.</p>`;
			}
			return `<p class="emne-dualempty">${tmpFilter ? 'Nothing matches that filter.' : 'Everything is selected.'}</p>`;
		}
		const tmpShown = tmpMatches.slice(0, AVAILABLE_RENDER_CAP);
		let tmpHTML = tmpShown.map((pOption) =>
			`<button type="button" class="emne-dualitem" ${tmpDisabled} onclick="_Pict.views.EmailNotificationsEditor.onDualAdd('${pID}', this.getAttribute('data-val'))" data-val="${this._esc(pOption.Value)}">${this._esc(pOption.Label)}</button>`).join('');
		if (tmpMatches.length > tmpShown.length)
		{
			tmpHTML += `<p class="emne-dualempty">${tmpMatches.length - tmpShown.length} more — narrow the filter to see them.</p>`;
		}
		return tmpHTML;
	}

	/** Options not currently selected, filtered by the pane's filter text. */
	_dualAvailableMatches(pID, pSpec)
	{
		const tmpSelected = {};
		pSpec.Read().forEach((pValue) => { tmpSelected[String(pValue)] = true; });
		if (pSpec.ServerSearch)
		{
			// Server-search lists show the latest server results (already filtered by the term), minus what's selected.
			return ((this._serverResults || {})[pID] || []).filter((pOption) => !tmpSelected[String(pOption.Value)]);
		}
		const tmpFilter = String((this._dualFilters || {})[pID] || '').trim().toLowerCase();
		return pSpec.Options().filter((pOption) =>
		{
			if (tmpSelected[String(pOption.Value)]) { return false; }
			if (!tmpFilter) { return true; }
			return (String(pOption.Label).toLowerCase().indexOf(tmpFilter) > -1) || (String(pOption.Value).toLowerCase().indexOf(tmpFilter) > -1);
		});
	}

	/**
	 * The selected side, driven by the rule. A value the source list does not contain still renders (by id,
	 * flagged) so it survives the round trip instead of being dropped.
	 */
	_dualSelectedHTML(pID, pSpec)
	{
		const tmpDisabled = this.provider.editorState.Editable ? '' : 'disabled';
		const tmpAllSelected = pSpec.Read();
		if (!tmpAllSelected.length)
		{
			return `<p class="emne-dualempty">None selected — the rule applies to <strong>all ${this._esc(pSpec.Label)}</strong>.</p>`;
		}
		const tmpKnown = {};
		pSpec.Options().forEach((pOption) => { tmpKnown[String(pOption.Value)] = pOption.Label; });
		const tmpSelected = this._dualSelectedMatches(pID, pSpec);
		if (!tmpSelected.length)
		{
			return `<p class="emne-dualempty">None of the ${tmpAllSelected.length} selected ${this._esc(pSpec.Label)} match that filter.</p>`;
		}
		return tmpSelected.map((pValue) =>
		{
			const tmpIsKnown = Object.prototype.hasOwnProperty.call(tmpKnown, String(pValue));
			const tmpLabel = tmpIsKnown ? tmpKnown[String(pValue)] : pSpec.LabelFor(pValue);
			const tmpFlag = tmpIsKnown ? '' : `<span class="emne-dualunknown" title="Not in the current list — kept so it is not lost."><i class="fas fa-circle-question"></i></span>`;
			return `<button type="button" class="emne-dualitem emne-dualitem-selected" ${tmpDisabled} onclick="_Pict.views.EmailNotificationsEditor.onDualRemove('${pID}', this.getAttribute('data-val'))" data-val="${this._esc(pValue)}"><span>${this._esc(tmpLabel)}</span>${tmpFlag}<i class="fas fa-xmark emne-dualx"></i></button>`;
		}).join('');
	}

	/** Selected values narrowed by the selected pane's own filter. */
	_dualSelectedMatches(pID, pSpec)
	{
		const tmpFilter = String((this._dualSelectedFilters || {})[pID] || '').trim().toLowerCase();
		const tmpSelected = pSpec.Read();
		if (!tmpFilter) { return tmpSelected; }
		const tmpKnown = {};
		pSpec.Options().forEach((pOption) => { tmpKnown[String(pOption.Value)] = pOption.Label; });
		return tmpSelected.filter((pValue) =>
		{
			const tmpLabel = Object.prototype.hasOwnProperty.call(tmpKnown, String(pValue)) ? tmpKnown[String(pValue)] : pSpec.LabelFor(pValue);
			return (String(tmpLabel).toLowerCase().indexOf(tmpFilter) > -1) || (String(pValue).toLowerCase().indexOf(tmpFilter) > -1);
		});
	}

	onDualSelectedFilter(pID, pFilter)
	{
		if (!this._dualSelectedFilters) { this._dualSelectedFilters = {}; }
		this._dualSelectedFilters[pID] = pFilter;
		const tmpSelected = document.getElementById(`emne-dualsel-${pID}`);
		if (tmpSelected) { tmpSelected.innerHTML = this._dualSelectedHTML(pID, this.dualLists[pID]); }
	}

	/** Repaint just the two list panes (never the filter inputs, so typing keeps focus). */
	_refreshDualPanes(pID)
	{
		const tmpSpec = this.dualLists[pID];
		const tmpAvailable = document.getElementById(`emne-dualavail-${pID}`);
		const tmpSelected = document.getElementById(`emne-dualsel-${pID}`);
		if (tmpAvailable) { tmpAvailable.innerHTML = this._dualAvailableHTML(pID, tmpSpec); }
		if (tmpSelected) { tmpSelected.innerHTML = this._dualSelectedHTML(pID, tmpSpec); }
		const tmpSelectedCount = document.getElementById(`emne-dualselcount-${pID}`);
		if (tmpSelectedCount) { tmpSelectedCount.textContent = String(tmpSpec.Read().length); }
		const tmpAvailableCount = document.getElementById(`emne-dualavailcount-${pID}`);
		if (tmpAvailableCount) { tmpAvailableCount.textContent = String(this._dualAvailableMatches(pID, tmpSpec).length); }
	}

	/** Coerce a value read back off a DOM attribute to the type the rule field stores. */
	_dualCoerce(pID, pValue)
	{
		return this.dualLists[pID].Numeric ? Number(pValue) : String(pValue);
	}

	onDualFilter(pID, pFilter)
	{
		if (!this._dualFilters) { this._dualFilters = {}; }
		this._dualFilters[pID] = pFilter;
		const tmpSpec = this.dualLists[pID];
		if (tmpSpec.ServerSearch) { this._runDualServerSearch(pID, tmpSpec, pFilter); return; }
		this._refreshDualAvailable(pID);
	}

	/** Debounced (250ms) server search for a ServerSearch dual-list; stale-token guarded; repaints the Available pane. */
	_runDualServerSearch(pID, pSpec, pFilter)
	{
		if (!this._serverResults) { this._serverResults = {}; }
		if (!this._dualSearchTimers) { this._dualSearchTimers = {}; }
		if (!this._dualSearchTokens) { this._dualSearchTokens = {}; }
		if (!this._dualSearching) { this._dualSearching = {}; }
		if (this._dualSearchTimers[pID]) { clearTimeout(this._dualSearchTimers[pID]); }
		const tmpTerm = String(pFilter == null ? '' : pFilter).trim();
		if (!tmpTerm)
		{
			this._serverResults[pID] = [];
			this._dualSearching[pID] = false;
			this._refreshDualAvailable(pID);
			return;
		}
		this._dualSearchTimers[pID] = setTimeout(() =>
		{
			this._dualSearchTimers[pID] = null;
			const tmpToken = (this._dualSearchTokens[pID] = (this._dualSearchTokens[pID] || 0) + 1);
			this._dualSearching[pID] = true;
			this._refreshDualAvailable(pID);
			Promise.resolve(pSpec.ServerSearch.Search(tmpTerm)).then((pResults) =>
			{
				if (tmpToken !== this._dualSearchTokens[pID]) { return; }   // superseded by a newer keystroke
				this._serverResults[pID] = Array.isArray(pResults) ? pResults : [];
				this._dualSearching[pID] = false;
				this._refreshDualAvailable(pID);
			}).catch(() =>
			{
				if (tmpToken !== this._dualSearchTokens[pID]) { return; }
				this._serverResults[pID] = [];
				this._dualSearching[pID] = false;
				this._refreshDualAvailable(pID);
			});
		}, 250);
	}

	/** Repaint just the Available pane + its count (leaves the filter input alone so typing keeps focus). */
	_refreshDualAvailable(pID)
	{
		const tmpSpec = this.dualLists[pID];
		const tmpAvailable = document.getElementById(`emne-dualavail-${pID}`);
		if (tmpAvailable) { tmpAvailable.innerHTML = this._dualAvailableHTML(pID, tmpSpec); }
		const tmpAvailableCount = document.getElementById(`emne-dualavailcount-${pID}`);
		if (tmpAvailableCount) { tmpAvailableCount.textContent = String(this._dualAvailableMatches(pID, tmpSpec).length); }
	}

	onDualAdd(pID, pValue)
	{
		const tmpSpec = this.dualLists[pID];
		const tmpValue = this._dualCoerce(pID, pValue);
		const tmpSelected = tmpSpec.Read().slice();
		if (!tmpSelected.some((pEntry) => String(pEntry) === String(tmpValue))) { tmpSelected.push(tmpValue); }
		tmpSpec.Write(tmpSelected);
		this._afterDualChange(pID);
	}

	onDualRemove(pID, pValue)
	{
		const tmpSpec = this.dualLists[pID];
		tmpSpec.Write(tmpSpec.Read().filter((pEntry) => String(pEntry) !== String(pValue)));
		this._afterDualChange(pID);
	}

	/** Add every option currently matching the filter — the bulk path for "all projects in a district". */
	onDualAddAll(pID)
	{
		const tmpSpec = this.dualLists[pID];
		if (tmpSpec.ServerSearch)
		{
			// Server-side sweep of the WHOLE current filter (not just the rendered page). With no filter there's
			// nothing meaningful to bulk-add (leave the selection empty to mean "all"), so this is a no-op.
			const tmpTerm = String((this._dualFilters || {})[pID] || '').trim();
			if (!tmpTerm) { return; }
			Promise.resolve(tmpSpec.ServerSearch.SearchAll(tmpTerm)).then((pResults) =>
			{
				const tmpSelected = tmpSpec.Read().slice();
				const tmpSeen = {};
				tmpSelected.forEach((pValue) => { tmpSeen[String(pValue)] = true; });
				(Array.isArray(pResults) ? pResults : []).forEach((pOption) => { if (!tmpSeen[String(pOption.Value)]) { tmpSeen[String(pOption.Value)] = true; tmpSelected.push(pOption.Value); } });
				tmpSpec.Write(tmpSelected);
				this._afterDualChange(pID);
			});
			return;
		}
		const tmpSelected = tmpSpec.Read().slice();
		this._dualAvailableMatches(pID, tmpSpec).forEach((pOption) => tmpSelected.push(pOption.Value));
		tmpSpec.Write(tmpSelected);
		this._afterDualChange(pID);
	}

	/** Remove what the Selected pane is showing — everything, unless its filter has narrowed the list. */
	onDualClear(pID)
	{
		const tmpSpec = this.dualLists[pID];
		const tmpRemoving = {};
		this._dualSelectedMatches(pID, tmpSpec).forEach((pValue) => { tmpRemoving[String(pValue)] = true; });
		tmpSpec.Write(tmpSpec.Read().filter((pValue) => !tmpRemoving[String(pValue)]));
		this._afterDualChange(pID);
	}

	_afterDualChange(pID)
	{
		this.provider._markRuleDirty();
		this._refreshDualPanes(pID);
		this._renderActions();
	}

	// --- Schedule section ---------------------------------------------------------------------------

	_scheduleHTML(pRule, pEditor)
	{
		const tmpDisabled = pEditor.Editable ? '' : 'disabled';
		const tmpType = pRule.ScheduleType || 'Weekly';
		const tmpTypeOptions = [ 'Daily', 'Weekly', 'Monthly' ].map((pType) => `<option value="${pType}" ${pType === tmpType ? 'selected' : ''}>${pType}</option>`).join('');
		let tmpHours = '';
		for (let i = 0; i < 24; i++) { tmpHours += `<option value="${i}" ${Number(pRule.ScheduleHour) === i ? 'selected' : ''}>${('0' + i).slice(-2)}:00 UTC</option>`; }
		let tmpDays = '';
		if (tmpType === 'Daily')
		{
			tmpDays = `<p class="emne-help">Sends every day at the chosen hour.</p>`;
		}
		else
		{
			const tmpSelected = pRule.ScheduleDays || [];
			// Each box's id is the value SetSchedule stores, as services/email/source/emailSources/scheduledRules.js matches it: weekdays 0-6 from Sunday, days of the month 1-31.
			const tmpFirst = (tmpType === 'Monthly') ? 1 : 0;
			const tmpLast = (tmpType === 'Monthly') ? 31 : 6;
			let tmpBoxes = '';
			for (let i = tmpFirst; i <= tmpLast; i++)
			{
				const tmpLabel = (tmpType === 'Monthly') ? String(i) : WEEKDAY_LABELS[i];
				tmpBoxes += `<label class="emne-day"><input type="checkbox" id="emne-sched-day-${i}" ${tmpSelected.indexOf(i) > -1 ? 'checked' : ''} ${tmpDisabled} onchange="_Pict.views.EmailNotificationsEditor.onDynamicChanged()"> ${tmpLabel}</label>`;
			}
			tmpDays = `<div class="emne-days" id="emne-sched-days" data-emne-sched-type="${tmpType}">${tmpBoxes}</div>`;
		}
		return `
			<div class="emne-divider"></div>
			<div class="emne-section-title"><span class="icon is-small"><i class="fas fa-clock"></i></span> Schedule</div>
			<div class="emne-field">
				<label class="emne-label">Frequency</label>
				<div class="select is-fullwidth"><select id="emne-sched-type" ${tmpDisabled} onchange="_Pict.views.EmailNotificationsEditor.onScheduleTypeChanged()">${tmpTypeOptions}</select></div>
			</div>
			<div class="emne-field">
				<label class="emne-label">Hour of day</label>
				<div class="select is-fullwidth"><select id="emne-sched-hour" ${tmpDisabled} onchange="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">${tmpHours}</select></div>
			</div>
			<div class="emne-field"><label class="emne-label">${tmpType === 'Monthly' ? 'Days of month' : (tmpType === 'Weekly' ? 'Days of week' : 'Days')}</label>${tmpDays}</div>`;
	}

	_captureScheduleFromDOM()
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule || !document.getElementById('emne-sched-type')) { return; }
		tmpRule.ScheduleType = document.getElementById('emne-sched-type').value;
		if (document.getElementById('emne-sched-hour')) { tmpRule.ScheduleHour = Number(document.getElementById('emne-sched-hour').value) || 0; }
		const tmpDays = [];
		// Straight after a frequency change the boxes on screen are still the old frequency's, and _renderDynamic captures again before replacing them; their ticks don't carry over.
		const tmpDaysElement = document.getElementById('emne-sched-days');
		const tmpBoxes = (tmpDaysElement && (tmpDaysElement.getAttribute('data-emne-sched-type') === tmpRule.ScheduleType)) ? tmpDaysElement.querySelectorAll('[id^="emne-sched-day-"]') : [];
		Array.prototype.forEach.call(tmpBoxes, (pBox) =>
		{
			if (pBox.checked) { tmpDays.push(parseInt(pBox.id.replace('emne-sched-day-', ''), 10)); }
		});
		tmpRule.ScheduleDays = tmpDays;
	}

	onScheduleTypeChanged()
	{
		this._captureScheduleFromDOM();
		this.provider._markRuleDirty();
		this._renderDynamic(); // the day controls change with the frequency
		this._renderActions();
	}

	// --- Config (custom content) section ------------------------------------------------------------
	//
	// Overrides are PER KEY. Every field the handler declares is listed with a "Customize" switch: off shows
	// the handler's built-in default (read only) and stores nothing, on seeds an editable copy and writes just
	// that key to the Configuration blob. Each field renders a TYPED editor picked by the field-schema module
	// — recipient rows, filter pairs, role checkboxes, tag chips, routing rows — instead of a JSON textarea.

	_configHTML(pRule, pEditor)
	{
		const tmpMeta = (this.provider.state.Handlers || {})[pRule.Handler] || {};
		const tmpHasDefaults = tmpMeta.defaultConfiguration && Object.keys(tmpMeta.defaultConfiguration).length > 0;
		let tmpHTML = `
			<div class="emne-divider"></div>
			<div class="emne-section-title"><span class="icon is-small"><i class="fas fa-envelope-open-text"></i></span> Email content</div>`;
		if (!tmpHasDefaults)
		{
			return tmpHTML + `<p class="emne-help">This email type has no editable content.</p>`;
		}
		tmpHTML += `<p class="emne-help mb-4">Each setting below runs on this email type's built-in default until you switch it to <strong>Customize</strong>. Only the settings you customize are stored on this rule.</p>`;

		this._activeBodyFields = [];
		const tmpDescriptors = this.provider.configFieldDescriptors(pRule.Handler, pRule.ConfigValue || {});
		tmpDescriptors.forEach((pField) =>
		{
			tmpHTML += `<div class="emne-cfgfield" id="emne-fld-${libConfigFieldSchema.fieldSlug(pField.Key)}">${this._configFieldInnerHTML(pField, pEditor)}</div>`;
		});
		return tmpHTML;
	}

	/** Re-render one config field in place (used by the add/remove/kind-change handlers). */
	_renderConfigField(pKey)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pKey);
		if (!this.pict.ContentAssignment.getElement(`#emne-fld-${tmpSlug}`)) { return; }
		const tmpEditor = this.provider.editorState;
		const tmpRule = tmpEditor.Rule;
		const tmpField = this.provider.configFieldDescriptors(tmpRule.Handler, tmpRule.ConfigValue || {}).find((pField) => pField.Key === pKey);
		if (!tmpField) { return; }
		// This field's body editor (if any) is about to be destroyed — drop it from the active list first so a
		// later capture doesn't marshal a stale, unmounted editor over the value we just wrote.
		this._activeBodyFields = (this._activeBodyFields || []).filter((pActive) => pActive !== pKey);
		this.pict.ContentAssignment.assignContent(`#emne-fld-${tmpSlug}`, this._configFieldInnerHTML(tmpField, tmpEditor));
		this._mountBodyEditorForKey(pKey);
		this.pict.CSSMap.injectCSS();
	}

	/** The head (label + Customize switch + docs) and body (default preview, or the typed editor) of one field. */
	_configFieldInnerHTML(pField, pEditor)
	{
		const tmpDisabled = pEditor.Editable ? '' : 'disabled';
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		const tmpDoc = pField.Doc ? `<p class="emne-help">${this._esc(pField.Doc)}</p>` : '';
		const tmpUnknown = pField.KnownField ? '' : `<span class="emne-badge emne-badge-warn" title="This setting is stored on the rule but this email type no longer declares it.">unrecognized</span>`;
		const tmpSwitch = `<label class="emne-override"><input type="checkbox" id="emne-ovr-${tmpSlug}" ${pField.Overridden ? 'checked' : ''} ${tmpDisabled} onchange="_Pict.views.EmailNotificationsEditor.onOverrideToggle('${this._attr(pField.Key)}', this.checked)"> Customize</label>`;
		const tmpHead = `<div class="emne-cfghead"><label class="emne-label">${this._esc(this._fieldLabel(pField.Key))}</label>${tmpUnknown}<span class="emne-spacer"></span>${tmpSwitch}</div>`;
		const tmpBody = pField.Overridden ? this._configEditorHTML(pField, tmpDisabled) : this._defaultPreviewHTML(pField);
		return `${tmpHead}${tmpDoc}<div class="emne-cfgbody">${tmpBody}</div>`;
	}

	/** Field keys are CamelCase identifiers from the handler; space them out for the label. */
	_fieldLabel(pKey)
	{
		return String(pKey).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^HTML /, 'HTML ');
	}

	/** What "use the built-in default" means for this field, shown read-only while the switch is off. */
	_defaultPreviewHTML(pField)
	{
		const tmpValue = pField.Default;
		if (tmpValue === undefined) { return `<p class="emne-default emne-default-empty">Not set.</p>`; }
		if (typeof tmpValue === 'boolean') { return `<p class="emne-default">${tmpValue ? 'Yes' : 'No'}</p>`; }
		if (Array.isArray(tmpValue))
		{
			if (!tmpValue.length) { return `<p class="emne-default emne-default-empty">Empty — no restriction.</p>`; }
			return `<ul class="emne-default emne-default-list">${tmpValue.map((pEntry) => `<li>${this._esc(typeof pEntry === 'object' ? JSON.stringify(pEntry) : pEntry)}</li>`).join('')}</ul>`;
		}
		if (tmpValue && typeof tmpValue === 'object')
		{
			const tmpKeys = Object.keys(tmpValue);
			if (!tmpKeys.length) { return `<p class="emne-default emne-default-empty">Empty.</p>`; }
			return `<pre class="emne-default emne-default-pre">${this._esc(JSON.stringify(tmpValue, null, 2))}</pre>`;
		}
		const tmpString = String(tmpValue);
		if (!tmpString.trim()) { return `<p class="emne-default emne-default-empty">Empty.</p>`; }
		if (tmpString.indexOf('\n') > -1)
		{
			return `<details class="emne-default emne-default-details"><summary>Show the default template</summary><pre class="emne-default-pre">${this._esc(tmpString.trim())}</pre></details>`;
		}
		return `<p class="emne-default emne-default-pre">${this._esc(tmpString)}</p>`;
	}

	/** Dispatch to the typed editor for an overridden field. */
	_configEditorHTML(pField, pDisabled)
	{
		switch (pField.Editor)
		{
			case 'recipients':      return this._recipientsHTML(pField, pDisabled);
			case 'documentfilters': return this._documentFiltersHTML(pField, pDisabled);
			case 'roles':           return this._rolesHTML(pField, pDisabled);
			case 'tags':            return this._tagsHTML(pField, pDisabled);
			case 'emaillist':       return this._emailListHTML(pField, pDisabled);
			case 'sendmap':         return this._sendMapHTML(pField, pDisabled);
			case 'template':        return this._templateHTML(pField, pDisabled);
			case 'body':            return this._bodyHTML(pField, pDisabled);
			case 'checkbox':        return this._checkboxHTML(pField, pDisabled);
			case 'json':            return this._jsonHTML(pField, pDisabled);
			default:                return this._textHTML(pField, pDisabled);
		}
	}

	// --- Typed editors ------------------------------------------------------------------------------

	/**
	 * Recipients: one row per recipient, each row choosing a KIND. The stored value is a handlebars template
	 * string, so the row kinds are a readable front-end for the three shapes that actually occur in practice
	 * (a literal address, the handler's `ProjectRole:<level>` expansion, and a document-person token) plus a
	 * free-text escape hatch for anything else.
	 */
	_recipientsHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		const tmpRows = libConfigFieldSchema.parseRecipients(pField.Value);
		let tmpHTML = `<div class="emne-rows" id="emne-rows-${tmpSlug}">`;
		tmpRows.forEach((pRow, pIndex) => { tmpHTML += this._recipientRowHTML(pField.Key, tmpSlug, pRow, pIndex, pDisabled); });
		tmpHTML += `</div>`;
		if (!tmpRows.length) { tmpHTML += `<p class="emne-help emne-warn">No recipients — this rule will not send to anyone.</p>`; }
		tmpHTML += this._addRowButtonHTML(pField.Key, 'Add recipient', pDisabled);
		return tmpHTML;
	}

	_recipientRowHTML(pKey, pSlug, pRow, pIndex, pDisabled)
	{
		const tmpKinds =
		[
			{ Value: 'email', Label: 'Email address' },
			{ Value: 'role', Label: 'Project role' },
			{ Value: 'token', Label: 'Person on the document' },
			{ Value: 'template', Label: 'Custom template' },
		];
		const tmpKindSelect = `<div class="select is-small"><select class="emne-rk" ${pDisabled} onchange="_Pict.views.EmailNotificationsEditor.onRecipientKindChanged('${this._attr(pKey)}', ${pIndex}, this.value)">`
			+ tmpKinds.map((pKind) => `<option value="${pKind.Value}" ${pKind.Value === pRow.Kind ? 'selected' : ''}>${pKind.Label}</option>`).join('')
			+ `</select></div>`;
		let tmpValueControl = '';
		if (pRow.Kind === 'role')
		{
			const tmpOptions = this.provider.permissionLevelOptions.slice();
			// A level that no role uses any more must still be selectable, or opening the rule would silently
			// rewrite the recipient to whatever happened to be first in the list.
			if (pRow.Value && !tmpOptions.some((pOption) => pOption.PermissionLevel === pRow.Value))
			{
				tmpOptions.unshift({ PermissionLevel: pRow.Value, Label: `${pRow.Value} (no matching role)` });
			}
			tmpValueControl = `<div class="select is-small is-fullwidth"><select class="emne-rv" ${pDisabled} onchange="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`
				+ (tmpOptions.length ? '' : `<option value="">No roles found</option>`)
				+ tmpOptions.map((pOption) => `<option value="${this._esc(pOption.PermissionLevel)}" ${pOption.PermissionLevel === pRow.Value ? 'selected' : ''}>${this._esc(pOption.Label)}</option>`).join('')
				+ `</select></div>`;
		}
		else if (pRow.Kind === 'token')
		{
			tmpValueControl = `<div class="select is-small is-fullwidth"><select class="emne-rv" ${pDisabled} onchange="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`
				+ libConfigFieldSchema.RECIPIENT_TOKENS.map((pToken) => `<option value="${this._esc(pToken.Value)}" ${pToken.Value === pRow.Value ? 'selected' : ''}>${this._esc(pToken.Label)}</option>`).join('')
				+ `</select></div>`;
		}
		else
		{
			const tmpPlaceholder = (pRow.Kind === 'email') ? 'name@example.com' : '{{Project.Email}}';
			tmpValueControl = `<input type="text" class="input is-small emne-rv" value="${this._esc(pRow.Value)}" placeholder="${tmpPlaceholder}" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`;
		}
		const tmpWarn = (pRow.Kind === 'email' && pRow.Value && !libConfigFieldSchema.looksLikeEmail(pRow.Value))
			? `<span class="emne-rowwarn" title="This does not look like an email address."><i class="fas fa-triangle-exclamation"></i></span>` : '';
		return `<div class="emne-row" data-emne-row="${pSlug}">${tmpKindSelect}<span class="emne-rowvalue">${tmpValueControl}</span>${tmpWarn}${this._removeRowButtonHTML(pKey, pIndex, pDisabled)}</div>`;
	}

	/** FilterDocuments: `[field, value]` pairs, edited as two columns. */
	_documentFiltersHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		const tmpRows = libConfigFieldSchema.parseDocumentFilters(pField.Value);
		let tmpHTML = `<datalist id="emne-dl-${tmpSlug}">`
			+ libConfigFieldSchema.DOCUMENT_FILTER_FIELDS.map((pName) => `<option value="${this._esc(pName)}"></option>`).join('')
			+ `</datalist><div class="emne-rows" id="emne-rows-${tmpSlug}">`;
		tmpRows.forEach((pRow, pIndex) =>
		{
			tmpHTML += `<div class="emne-row" data-emne-row="${tmpSlug}">`
				+ `<input type="text" class="input is-small emne-ff" list="emne-dl-${tmpSlug}" value="${this._esc(pRow.Field)}" placeholder="Field" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`
				+ `<span class="emne-rowop">is</span>`
				+ `<input type="text" class="input is-small emne-fv" value="${this._esc(pRow.Value)}" placeholder="Value" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`
				+ `${this._removeRowButtonHTML(pField.Key, pIndex, pDisabled)}</div>`;
		});
		tmpHTML += `</div>`;
		if (!tmpRows.length) { tmpHTML += `<p class="emne-help">No filters — every document this rule matches is included.</p>`; }
		tmpHTML += this._addRowButtonHTML(pField.Key, 'Add filter', pDisabled);
		return tmpHTML;
	}

	/**
	 * UsersWithRole: a checkbox per PermissionLevel. Entries stored as numeric IDRoles are shown as their own
	 * checked rows so they survive a round-trip — rewriting them to permission levels would change who matches.
	 */
	_rolesHTML(pField, pDisabled)
	{
		const tmpSelected = libConfigFieldSchema.parseRoleList(pField.Value);
		const tmpOptions = this.provider.permissionLevelOptions.map((pOption) => ({ Value: pOption.PermissionLevel, Label: pOption.Label, Numeric: false }));
		tmpSelected.forEach((pEntry) =>
		{
			const tmpNumeric = (typeof pEntry === 'number') || /^\d+$/.test(String(pEntry));
			if (!tmpOptions.some((pOption) => String(pOption.Value) === String(pEntry)))
			{
				tmpOptions.push({ Value: pEntry, Label: this.provider.roleEntryLabel(pEntry), Numeric: tmpNumeric });
			}
		});
		if (!tmpOptions.length) { return `<p class="emne-help">No roles found for this customer.</p>`; }
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		// Selected levels sort to the top so a choice is never lost below the fold in a long list.
		tmpOptions.sort((pA, pB) =>
		{
			const tmpASelected = tmpSelected.some((pEntry) => String(pEntry) === String(pA.Value)) ? 0 : 1;
			const tmpBSelected = tmpSelected.some((pEntry) => String(pEntry) === String(pB.Value)) ? 0 : 1;
			return (tmpASelected - tmpBSelected) || String(pA.Label).localeCompare(String(pB.Label));
		});
		const tmpBoxes = tmpOptions.map((pOption) =>
		{
			const tmpChecked = tmpSelected.some((pEntry) => String(pEntry) === String(pOption.Value)) ? 'checked' : '';
			return `<label class="emne-checkitem" data-filter="${this._esc(String(pOption.Label).toLowerCase())} ${this._esc(String(pOption.Value).toLowerCase())}"><input type="checkbox" class="emne-role" data-emne-roleval="${this._esc(pOption.Value)}" data-emne-rolenum="${pOption.Numeric ? '1' : '0'}" ${tmpChecked} ${pDisabled} onchange="_Pict.views.EmailNotificationsEditor.onDynamicChanged()"> ${this._esc(pOption.Label)}</label>`;
		}).join('');
		const tmpNote = tmpSelected.length ? '' : `<p class="emne-help">Nothing selected — <strong>any</strong> role matches.</p>`;
		// A customer can have well over a hundred permission levels, so the list gets an in-place filter over
		// the already-rendered rows (no refetch, and the checkbox state of a hidden row is still captured).
		const tmpFilter = (tmpOptions.length > 12)
			? `<input type="text" class="input is-small emne-checkfilter" placeholder="Filter ${tmpOptions.length} roles…" oninput="_Pict.views.EmailNotificationsEditor.onFilterCheckList('${tmpSlug}', this.value)">` : '';
		return `${tmpFilter}<div class="emne-checklist" id="emne-checklist-${tmpSlug}">${tmpBoxes}</div><p class="emne-help emne-nomatch" id="emne-nomatch-${tmpSlug}" style="display:none;">No roles match that filter.</p>${tmpNote}`;
	}

	/** Actor tags: chips, with suggestions harvested from the customer's approval workflows. */
	_tagsHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		const tmpTags = libConfigFieldSchema.parseTagList(pField.Value);
		const tmpSuggestions = ((this.provider.state.Lookups || {}).ActorTags || []).filter((pTag) => tmpTags.indexOf(pTag) < 0);
		const tmpChips = tmpTags.map((pTag, pIndex) => this._chipHTML(pField.Key, pTag, pIndex, pDisabled)).join('');
		const tmpNote = tmpTags.length ? '' : `<p class="emne-help">No tags — this check is skipped.</p>`;
		return `<div class="emne-chips">${tmpChips}</div>${tmpNote}`
			+ `<datalist id="emne-dl-${tmpSlug}">${tmpSuggestions.map((pTag) => `<option value="${this._esc(pTag)}"></option>`).join('')}</datalist>`
			+ this._chipInputHTML(pField.Key, tmpSlug, 'Add a tag', pDisabled);
	}

	/** CC / BCC / SendRestTo: comma-separated email strings, edited as chips. */
	_emailListHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		const tmpChipValues = libConfigFieldSchema.parseEmailList(pField.Value);
		const tmpChips = tmpChipValues.map((pEmail, pIndex) => this._chipHTML(pField.Key, pEmail, pIndex, pDisabled, !libConfigFieldSchema.looksLikeEmail(pEmail))).join('');
		const tmpNote = tmpChipValues.length ? '' : `<p class="emne-help">Empty — nobody is added.</p>`;
		return `<div class="emne-chips">${tmpChips}</div>${tmpNote}` + this._chipInputHTML(pField.Key, tmpSlug, 'name@example.com', pDisabled);
	}

	/** ApprovalRollup's SendTo: a routing map from a BasedOn result to a literal address list. */
	_sendMapHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		const tmpRows = libConfigFieldSchema.parseSendMap(pField.Value);
		let tmpHTML = `<div class="emne-rows" id="emne-rows-${tmpSlug}">`;
		tmpRows.forEach((pRow, pIndex) =>
		{
			tmpHTML += `<div class="emne-row emne-row-map" data-emne-row="${tmpSlug}">`
				+ `<input type="text" class="input is-small emne-mk" value="${this._esc(pRow.Key)}" placeholder="Grouping value" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`
				+ `<span class="emne-rowop">&rarr;</span>`
				+ `<input type="text" class="input is-small emne-mv" value="${this._esc(pRow.Value)}" placeholder="a@example.com, b@example.com" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`
				+ `${this._removeRowButtonHTML(pField.Key, pIndex, pDisabled)}</div>`;
		});
		tmpHTML += `</div>`;
		if (!tmpRows.length) { tmpHTML += `<p class="emne-help">No routes — everything falls through to <strong>Send Rest To</strong>.</p>`; }
		tmpHTML += this._addRowButtonHTML(pField.Key, 'Add route', pDisabled);
		return tmpHTML;
	}

	/** A single-line handlebars template, with a variable inserter built from the handler's own docs. */
	_templateHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		const tmpVariables = this._templateVariables();
		const tmpInsert = (tmpVariables.length && !pDisabled)
			? `<div class="select is-small"><select class="emne-insert" onchange="_Pict.views.EmailNotificationsEditor.onInsertVariable('${this._attr(pField.Key)}', this)">`
				+ `<option value="">Insert variable…</option>`
				+ tmpVariables.map((pVariable) => `<option value="${this._esc(pVariable)}">${this._esc(pVariable)}</option>`).join('')
				+ `</select></div>` : '';
		return `<div class="emne-inline">`
			+ `<input type="text" class="input emne-cfg" id="emne-cfg-${tmpSlug}" value="${this._esc(pField.Value == null ? '' : pField.Value)}" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`
			+ `${tmpInsert}</div>`;
	}

	/** The handlebars entities a handler documents, scraped from its `docs` string ("  {{Project}}" lines). */
	_templateVariables()
	{
		const tmpRule = this.provider.editorState.Rule;
		const tmpMeta = (this.provider.state.Handlers || {})[tmpRule.Handler] || {};
		const tmpDocs = String(tmpMeta.docs || '');
		const tmpFound = [];
		const tmpMatcher = /^\s+(\{\{[A-Za-z0-9_.]+\}\})/gm;
		let tmpMatch = tmpMatcher.exec(tmpDocs);
		while (tmpMatch)
		{
			if (tmpFound.indexOf(tmpMatch[1]) < 0) { tmpFound.push(tmpMatch[1]); }
			tmpMatch = tmpMatcher.exec(tmpDocs);
		}
		return tmpFound;
	}

	_checkboxHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		return `<label class="emne-enable-row"><input type="checkbox" class="emne-cfg" id="emne-cfg-${tmpSlug}" ${pField.Value ? 'checked' : ''} ${pDisabled} onchange="_Pict.views.EmailNotificationsEditor.onDynamicChanged()"> Yes</label>`;
	}

	_textHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		return `<input type="text" class="input emne-cfg" id="emne-cfg-${tmpSlug}" value="${this._esc(pField.Value == null ? '' : pField.Value)}" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">`;
	}

	/**
	 * The escape hatch: any array/object shape the field schema does not model. Invalid JSON is reported and
	 * BLOCKS the save — silently keeping the raw text would store a string where the handler expects a
	 * structure, and the rule would fail at send time with no sign of it here.
	 */
	_jsonHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		const tmpJSON = (typeof pField.Value === 'string') ? pField.Value : JSON.stringify(pField.Value == null ? '' : pField.Value, null, 2);
		return `<textarea class="textarea emne-json emne-cfg" id="emne-cfg-${tmpSlug}" rows="5" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">${this._esc(tmpJSON)}</textarea>`
			+ `<p class="emne-help">JSON value.</p><p class="emne-fielderror" id="emne-err-${tmpSlug}"></p>`;
	}

	/** Message bodies: a plain code editor — these are handlebars/HTML templates, not markdown. */
	_bodyHTML(pField, pDisabled)
	{
		const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
		if (this._codeEditorAvailable())
		{
			this._activeBodyFields.push(pField.Key);
			return `<div id="EmailBody-${tmpSlug}" class="emne-code"></div>`;
		}
		return `<textarea class="textarea emne-code-fallback emne-cfg" id="emne-cfg-${tmpSlug}" rows="12" ${pDisabled} oninput="_Pict.views.EmailNotificationsEditor.onDynamicChanged()">${this._esc(pField.Value == null ? '' : pField.Value)}</textarea>`;
	}

	// --- Shared row / chip chrome -------------------------------------------------------------------

	_addRowButtonHTML(pKey, pLabel, pDisabled)
	{
		if (pDisabled) { return ''; }
		return `<button type="button" class="button is-small is-light emne-rowadd" onclick="_Pict.views.EmailNotificationsEditor.onAddRow('${this._attr(pKey)}')"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>${this._esc(pLabel)}</span></button>`;
	}

	_removeRowButtonHTML(pKey, pIndex, pDisabled)
	{
		if (pDisabled) { return ''; }
		return `<button type="button" class="emne-rowdel" title="Remove" onclick="_Pict.views.EmailNotificationsEditor.onRemoveRow('${this._attr(pKey)}', ${pIndex})"><i class="fas fa-xmark"></i></button>`;
	}

	_chipHTML(pKey, pValue, pIndex, pDisabled, pWarn)
	{
		const tmpRemove = pDisabled ? '' : `<button type="button" class="emne-chipdel" title="Remove" onclick="_Pict.views.EmailNotificationsEditor.onRemoveRow('${this._attr(pKey)}', ${pIndex})"><i class="fas fa-xmark"></i></button>`;
		return `<span class="emne-chip ${pWarn ? 'emne-chip-warn' : ''}" ${pWarn ? 'title="This does not look like an email address."' : ''}>${this._esc(pValue)}${tmpRemove}</span>`;
	}

	_chipInputHTML(pKey, pSlug, pPlaceholder, pDisabled)
	{
		if (pDisabled) { return ''; }
		return `<div class="emne-chipadd">`
			+ `<input type="text" class="input is-small" id="emne-chipin-${pSlug}" list="emne-dl-${pSlug}" placeholder="${this._esc(pPlaceholder)}" onkeydown="_Pict.views.EmailNotificationsEditor.onChipKeyDown(event, '${this._attr(pKey)}')">`
			+ `<button type="button" class="button is-small is-light" onclick="_Pict.views.EmailNotificationsEditor.onAddChip('${this._attr(pKey)}')">Add</button></div>`;
	}

	// --- Config field events ------------------------------------------------------------------------

	/** The per-key Customize switch. */
	onOverrideToggle(pKey, pChecked)
	{
		this._captureDynamic();
		this.provider.setFieldOverride(pKey, !!pChecked);
		this._renderConfigField(pKey);
		this._renderActions();
	}

	/** Changing a recipient row's kind rebuilds that row's value control, so re-render the whole field. */
	onRecipientKindChanged(pKey, pIndex, pKind)
	{
		this._captureDynamic();
		const tmpRows = libConfigFieldSchema.parseRecipients(this._fieldValue(pKey));
		if (!tmpRows[pIndex]) { return; }
		// The old value rarely means anything in the new kind (an address is not a role), so start it on the
		// new kind's first option — except between the two free-text kinds, where keeping it is what the admin
		// expects. Rows are written through unfiltered so the row being edited survives even while it is blank.
		const tmpKeepValue = ((tmpRows[pIndex].Kind === 'email') && (pKind === 'template')) || ((tmpRows[pIndex].Kind === 'template') && (pKind === 'email'));
		tmpRows[pIndex] = { Kind: pKind, Value: tmpKeepValue ? tmpRows[pIndex].Value : this._defaultRowValue(pKind) };
		this._setFieldValue(pKey, tmpRows.map((pRow) => libConfigFieldSchema.serializeRecipient(pRow)));
		this.provider._markRuleDirty();
		this._renderConfigField(pKey);
		this._renderActions();
	}

	/** A fresh row of a given kind starts on the first sensible option, so the select is never out of sync. */
	_defaultRowValue(pKind)
	{
		if (pKind === 'role') { return (this.provider.permissionLevelOptions[0] || {}).PermissionLevel || ''; }
		if (pKind === 'token') { return libConfigFieldSchema.RECIPIENT_TOKENS[0].Value; }
		return '';
	}

	onAddRow(pKey)
	{
		this._captureDynamic();
		const tmpField = this._descriptorFor(pKey);
		if (!tmpField) { return; }
		const tmpValue = this._fieldValue(pKey);
		if (tmpField.Editor === 'recipients')
		{
			const tmpRows = libConfigFieldSchema.toArray(tmpValue).slice();
			tmpRows.push('');
			this._setFieldValue(pKey, tmpRows);
		}
		else if (tmpField.Editor === 'documentfilters')
		{
			const tmpRows = libConfigFieldSchema.parseDocumentFilters(tmpValue);
			tmpRows.push({ Field: '', Value: '' });
			// A brand-new blank row would be dropped by serialize, so write the rows through unfiltered.
			this._setFieldValue(pKey, tmpRows.map((pRow) => [ pRow.Field, pRow.Value ]));
		}
		else if (tmpField.Editor === 'sendmap')
		{
			const tmpRows = libConfigFieldSchema.parseSendMap(tmpValue);
			// An object cannot hold a blank key, so the placeholder key is unique-by-index until it's named.
			let tmpIndex = tmpRows.length + 1;
			while (tmpRows.some((pRow) => pRow.Key === `key${tmpIndex}`)) { tmpIndex++; }
			tmpRows.push({ Key: `key${tmpIndex}`, Value: '' });
			this._setFieldValue(pKey, libConfigFieldSchema.serializeSendMap(tmpRows));
		}
		this.provider._markRuleDirty();
		this._renderConfigField(pKey);
		this._renderActions();
	}

	onRemoveRow(pKey, pIndex)
	{
		this._captureDynamic();
		const tmpField = this._descriptorFor(pKey);
		if (!tmpField) { return; }
		const tmpValue = this._fieldValue(pKey);
		if (tmpField.Editor === 'sendmap')
		{
			const tmpRows = libConfigFieldSchema.parseSendMap(tmpValue);
			tmpRows.splice(pIndex, 1);
			this._setFieldValue(pKey, libConfigFieldSchema.serializeSendMap(tmpRows));
		}
		else if (tmpField.Editor === 'emaillist')
		{
			const tmpChips = libConfigFieldSchema.parseEmailList(tmpValue);
			tmpChips.splice(pIndex, 1);
			this._setFieldValue(pKey, libConfigFieldSchema.serializeEmailList(tmpChips));
		}
		else if (tmpField.Editor === 'documentfilters')
		{
			const tmpRows = libConfigFieldSchema.parseDocumentFilters(tmpValue);
			tmpRows.splice(pIndex, 1);
			this._setFieldValue(pKey, tmpRows.map((pRow) => [ pRow.Field, pRow.Value ]));
		}
		else
		{
			const tmpRows = libConfigFieldSchema.toArray(tmpValue).slice();
			tmpRows.splice(pIndex, 1);
			this._setFieldValue(pKey, tmpRows);
		}
		this.provider._markRuleDirty();
		this._renderConfigField(pKey);
		this._renderActions();
	}

	onChipKeyDown(pEvent, pKey)
	{
		if (pEvent && (pEvent.key === 'Enter' || pEvent.key === ','))
		{
			pEvent.preventDefault();
			this.onAddChip(pKey);
		}
	}

	onAddChip(pKey)
	{
		const tmpField = this._descriptorFor(pKey);
		if (!tmpField) { return; }
		const tmpInput = document.getElementById(`emne-chipin-${libConfigFieldSchema.fieldSlug(pKey)}`);
		const tmpEntry = tmpInput ? String(tmpInput.value || '').trim() : '';
		if (!tmpEntry) { return; }
		this._captureDynamic();
		if (tmpField.Editor === 'emaillist')
		{
			const tmpChips = libConfigFieldSchema.parseEmailList(this._fieldValue(pKey));
			// A pasted "a@x.com, b@y.com" should become two chips, not one.
			libConfigFieldSchema.parseEmailList(tmpEntry).forEach((pEntry) => tmpChips.push(pEntry));
			this._setFieldValue(pKey, libConfigFieldSchema.serializeEmailList(tmpChips));
		}
		else
		{
			const tmpTags = libConfigFieldSchema.parseTagList(this._fieldValue(pKey));
			tmpTags.push(tmpEntry);
			this._setFieldValue(pKey, libConfigFieldSchema.parseTagList(tmpTags));
		}
		this.provider._markRuleDirty();
		this._renderConfigField(pKey);
		this._renderActions();
		const tmpFocus = document.getElementById(`emne-chipin-${libConfigFieldSchema.fieldSlug(pKey)}`);
		if (tmpFocus) { tmpFocus.focus(); }
	}

	/**
	 * Filter an already-rendered checkbox list in place. Rows are only hidden, never removed, so a checked
	 * row that scrolls out of the filter is still read back by the capture pass.
	 */
	onFilterCheckList(pSlug, pFilter)
	{
		const tmpList = document.getElementById(`emne-checklist-${pSlug}`);
		if (!tmpList) { return; }
		const tmpNeedle = String(pFilter || '').trim().toLowerCase();
		let tmpVisible = 0;
		Array.prototype.forEach.call(tmpList.querySelectorAll('[data-filter]'), (pRow) =>
		{
			const tmpMatch = !tmpNeedle || (pRow.getAttribute('data-filter').indexOf(tmpNeedle) > -1);
			pRow.style.display = tmpMatch ? '' : 'none';
			if (tmpMatch) { tmpVisible++; }
		});
		const tmpEmpty = document.getElementById(`emne-nomatch-${pSlug}`);
		if (tmpEmpty) { tmpEmpty.style.display = tmpVisible ? 'none' : ''; }
	}

	/** Insert a handlebars variable at the cursor in a single-line template field. */
	onInsertVariable(pKey, pSelect)
	{
		const tmpVariable = pSelect ? pSelect.value : '';
		pSelect.value = '';
		if (!tmpVariable) { return; }
		const tmpInput = document.getElementById(`emne-cfg-${libConfigFieldSchema.fieldSlug(pKey)}`);
		if (!tmpInput) { return; }
		const tmpStart = (tmpInput.selectionStart == null) ? tmpInput.value.length : tmpInput.selectionStart;
		const tmpEnd = (tmpInput.selectionEnd == null) ? tmpInput.value.length : tmpInput.selectionEnd;
		tmpInput.value = tmpInput.value.slice(0, tmpStart) + tmpVariable + tmpInput.value.slice(tmpEnd);
		tmpInput.focus();
		tmpInput.setSelectionRange(tmpStart + tmpVariable.length, tmpStart + tmpVariable.length);
		this.onDynamicChanged();
	}

	// --- Config value plumbing ----------------------------------------------------------------------

	_descriptorFor(pKey)
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule) { return null; }
		return this.provider.configFieldDescriptors(tmpRule.Handler, tmpRule.ConfigValue || {}).find((pField) => pField.Key === pKey) || null;
	}

	_fieldValue(pKey)
	{
		const tmpField = this._descriptorFor(pKey);
		return tmpField ? tmpField.Value : undefined;
	}

	/** Writing always goes to ConfigValue — these helpers only run for fields that are already overridden. */
	_setFieldValue(pKey, pValue)
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule) { return; }
		if (!tmpRule.ConfigValue) { tmpRule.ConfigValue = {}; }
		tmpRule.ConfigValue[pKey] = pValue;
	}

	/** A plain field inside the dynamic region changed (no structural re-render needed). */
	onDynamicChanged()
	{
		this._captureDynamic();
		this.provider._markRuleDirty();
		this._renderActions();
	}

	/** Read schedule + config fields (incl. marshaling the code editors) into the working rule. */
	_captureDynamic()
	{
		this._captureScheduleFromDOM();
		this._captureConfigFromDOM();
	}

	/**
	 * Read every overridden config field out of the DOM. Non-overridden fields are skipped entirely — they
	 * have no controls, and must stay absent from ConfigValue so the handler default keeps applying.
	 */
	_captureConfigFromDOM()
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule || !this.provider.editorState.Editable) { return; }
		if (!document.getElementById('EmailNotifications-Dynamic') && !this.pict.ContentAssignment.getElement('#EmailNotifications-Dynamic')) { return; }
		if (!tmpRule.ConfigValue) { tmpRule.ConfigValue = {}; }
		this._configErrors = {};
		this.provider.configFieldDescriptors(tmpRule.Handler, tmpRule.ConfigValue).forEach((pField) =>
		{
			if (!pField.Overridden) { return; }
			const tmpSlug = libConfigFieldSchema.fieldSlug(pField.Key);
			// The field is not on the page (a different handler is showing) — leave the stored value alone.
			if (!document.getElementById(`emne-fld-${tmpSlug}`)) { return; }
			const tmpCaptured = this._captureConfigField(pField, tmpSlug);
			if (tmpCaptured !== undefined) { tmpRule.ConfigValue[pField.Key] = tmpCaptured; }
		});
	}

	/** Read one overridden field's controls. Returns undefined when the field owns its value outside the DOM. */
	_captureConfigField(pField, pSlug)
	{
		const tmpContainer = document.getElementById(`emne-fld-${pSlug}`);
		switch (pField.Editor)
		{
			case 'recipients':
			{
				const tmpRows = Array.prototype.map.call(tmpContainer.querySelectorAll('.emne-row'), (pRow) =>
				{
					const tmpKind = pRow.querySelector('.emne-rk');
					const tmpValue = pRow.querySelector('.emne-rv');
					return { Kind: tmpKind ? tmpKind.value : 'email', Value: tmpValue ? tmpValue.value : '' };
				});
				return libConfigFieldSchema.serializeRecipients(tmpRows);
			}
			case 'documentfilters':
			{
				const tmpRows = Array.prototype.map.call(tmpContainer.querySelectorAll('.emne-row'), (pRow) =>
				{
					const tmpFieldInput = pRow.querySelector('.emne-ff');
					const tmpValueInput = pRow.querySelector('.emne-fv');
					return { Field: tmpFieldInput ? tmpFieldInput.value : '', Value: tmpValueInput ? tmpValueInput.value : '' };
				});
				return libConfigFieldSchema.serializeDocumentFilters(tmpRows);
			}
			case 'roles':
			{
				const tmpSelected = [];
				Array.prototype.forEach.call(tmpContainer.querySelectorAll('.emne-role'), (pBox) =>
				{
					if (!pBox.checked) { return; }
					const tmpValue = pBox.getAttribute('data-emne-roleval');
					tmpSelected.push((pBox.getAttribute('data-emne-rolenum') === '1') ? Number(tmpValue) : tmpValue);
				});
				return tmpSelected;
			}
			case 'sendmap':
			{
				const tmpRows = Array.prototype.map.call(tmpContainer.querySelectorAll('.emne-row'), (pRow) =>
				{
					const tmpKeyInput = pRow.querySelector('.emne-mk');
					const tmpValueInput = pRow.querySelector('.emne-mv');
					return { Key: tmpKeyInput ? tmpKeyInput.value : '', Value: tmpValueInput ? tmpValueInput.value : '' };
				});
				return libConfigFieldSchema.serializeSendMap(tmpRows);
			}
			// Chips own their value in ConfigValue directly (add/remove re-render the field), so there is
			// nothing to read back off the page.
			case 'tags':
			case 'emaillist':
				return undefined;
			case 'body':
			{
				if (this._activeBodyFields.indexOf(pField.Key) > -1) { return this._captureBodyEditor(pField.Key); }
				const tmpFallback = document.getElementById(`emne-cfg-${pSlug}`);
				return tmpFallback ? tmpFallback.value : undefined;
			}
			case 'checkbox':
			{
				const tmpBox = document.getElementById(`emne-cfg-${pSlug}`);
				return tmpBox ? tmpBox.checked : undefined;
			}
			case 'json':
			{
				const tmpArea = document.getElementById(`emne-cfg-${pSlug}`);
				if (!tmpArea) { return undefined; }
				try
				{
					const tmpParsed = JSON.parse(tmpArea.value);
					this._setFieldError(pSlug, '');
					return tmpParsed;
				}
				catch (pError)
				{
					// Keep the raw text so the admin does not lose their edit, and record the error so save blocks.
					this._configErrors[pField.Key] = `${pField.Key} is not valid JSON.`;
					this._setFieldError(pSlug, 'Not valid JSON — fix this before saving.');
					return tmpArea.value;
				}
			}
			default:
			{
				const tmpInput = document.getElementById(`emne-cfg-${pSlug}`);
				return tmpInput ? tmpInput.value : undefined;
			}
		}
	}

	_setFieldError(pSlug, pMessage)
	{
		const tmpError = document.getElementById(`emne-err-${pSlug}`);
		if (tmpError) { tmpError.textContent = pMessage || ''; }
	}

	// --- Message-body code editors ------------------------------------------------------------------
	//
	// TextMessage / HTMLMessage are handlebars templates (one plaintext, one HTML) — NOT markdown — so they
	// get a plain CodeMirror-6 editor built straight off the vendored bundle (basicSetup only: line numbers,
	// history, bracket matching, no language mode and no rich preview). Without the bundle on the page they
	// fall back to a monospace textarea.

	/** The vendored CodeMirror-6 bundle, or null when it is not on the page. */
	get codeMirrorModules()
	{
		const tmpWindow = /** @type {any} */ ((typeof window === 'undefined') ? null : window);
		const tmpModules = tmpWindow && tmpWindow.CodeMirrorModules;
		return (tmpModules && (typeof tmpModules.EditorView === 'function') && (typeof tmpModules.EditorState === 'function')) ? tmpModules : null;
	}

	_codeEditorAvailable()
	{
		return !!this.codeMirrorModules;
	}

	_mountActiveBodyEditors()
	{
		(this._activeBodyFields || []).slice().forEach((pKey) => this._mountBodyEditor(pKey));
	}

	/** Mount the editor for a single key, if that key is a body field on the current page. */
	_mountBodyEditorForKey(pKey)
	{
		const tmpField = this._descriptorFor(pKey);
		if (!tmpField || !tmpField.Overridden || (tmpField.Editor !== 'body')) { return; }
		if (!this._codeEditorAvailable()) { return; }
		if (this._activeBodyFields.indexOf(pKey) < 0) { this._activeBodyFields.push(pKey); }
		this._mountBodyEditor(pKey);
	}

	_mountBodyEditor(pKey)
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule) { return; }
		const tmpTarget = document.getElementById(`EmailBody-${libConfigFieldSchema.fieldSlug(pKey)}`);
		if (!tmpTarget) { return; }
		const tmpValue = (tmpRule.ConfigValue || {})[pKey];
		try
		{
			const tmpCM = this.codeMirrorModules;
			if (!tmpCM) { return; }
			const tmpExtensions = [ tmpCM.basicSetup ];
			if (!this.provider.editorState.Editable) { tmpExtensions.push(tmpCM.EditorState.readOnly.of(true)); }
			else { tmpExtensions.push(tmpCM.EditorView.updateListener.of((pUpdate) => { if (pUpdate.docChanged) { this.onBodyEditorChanged(); } })); }
			if (!this._bodyEditors) { this._bodyEditors = {}; }
			// Re-mounting after a field re-render: drop the old instance so its DOM and listeners go away.
			if (this._bodyEditors[pKey]) { try { this._bodyEditors[pKey].destroy(); } catch (pError) { /* already gone */ } }
			tmpTarget.innerHTML = '';
			this._bodyEditors[pKey] = new tmpCM.EditorView(
				{
					state: tmpCM.EditorState.create({ doc: (tmpValue == null) ? '' : String(tmpValue), extensions: tmpExtensions }),
					parent: tmpTarget,
				});
		}
		catch (pError)
		{
			this.pict.log.warn(`Email body code editor failed to mount`, { key: pKey, error: String(pError) });
			// Leave the container empty rather than a half-built editor; the value is still in ConfigValue.
			this._activeBodyFields = (this._activeBodyFields || []).filter((pActive) => pActive !== pKey);
		}
	}

	/** A code editor's document changed — mark dirty without a re-render (that would tear the editor down). */
	onBodyEditorChanged()
	{
		this.provider._markRuleDirty();
		this._renderActions();
	}

	_captureBodyEditor(pKey)
	{
		const tmpEditor = (this._bodyEditors || {})[pKey];
		if (!tmpEditor || !tmpEditor.state) { return undefined; }
		return tmpEditor.state.doc.toString();
	}

	/** Tear down every mounted code editor (the whole region is about to be replaced). */
	_destroyBodyEditors()
	{
		Object.keys(this._bodyEditors || {}).forEach((pKey) =>
		{
			try { this._bodyEditors[pKey].destroy(); } catch (pError) { /* already gone */ }
		});
		this._bodyEditors = {};
	}

	/** Capture base fields + dynamic region into the working rule. */
	_captureAll()
	{
		this._captureForm();
		this._captureDynamic();
	}
	// --- Actions / persistence ----------------------------------------------------------------------

	_renderActions()
	{
		if (!this.pict.ContentAssignment.getElement('#EmailNotifications-Editor-Actions')) { return; }
		const tmpEditor = this.provider.editorState;
		const tmpRecord =
		{
			EditSlot: tmpEditor.Editable ? [ {} ] : [],
			ReadOnlySlot: (!tmpEditor.Editable && !tmpEditor.Loading && !tmpEditor.Error) ? [ {} ] : [],
			DirtySlot: (tmpEditor.Editable && tmpEditor.Dirty) ? [ {} ] : [],
		};
		this.pict.ContentAssignment.assignContent('#EmailNotifications-Editor-Actions', this.pict.parseTemplateByHash('EmailNotifications-Editor-Actions-Template', tmpRecord));
	}

	async saveRule()
	{
		this._captureAll();
		// A JSON escape-hatch field that did not parse blocks the save — storing the raw text would put a
		// string where the handler expects a structure, and the rule would fail silently at send time.
		const tmpErrorKeys = Object.keys(this._configErrors || {});
		if (tmpErrorKeys.length)
		{
			this.modal.toast(`Fix ${tmpErrorKeys.length === 1 ? 'this setting' : 'these settings'} before saving: ${tmpErrorKeys.join(', ')}.`, { type: 'warning', duration: 6000 });
			return;
		}
		try
		{
			const tmpResult = await this.provider.saveRuleAsync();
			if (tmpResult && tmpResult.Error) { this.modal.toast(tmpResult.Error, { type: 'warning' }); return; }
			// A partial save (rule written, custom content rejected) stays on the page — navigating away would
			// hide that the content the admin just edited is not what the rule will actually send.
			if (tmpResult && tmpResult.Warning)
			{
				this.modal.toast(tmpResult.Warning, { type: 'warning', duration: 9000 });
				return;
			}
			this.modal.toast('Email rule saved.', { type: 'success' });
			window.location.hash = '#/EmailNotifications';
		}
		catch (pError)
		{
			this.modal.toast(`Save failed: ${pError && pError.message ? pError.message : pError}`, { type: 'error', duration: 6000 });
		}
	}

	cancel()
	{
		window.location.hash = '#/EmailNotifications';
	}

	async deleteRule()
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule || !tmpRule.IDEmailRule) { return; }
		const tmpOk = await this.modal.confirm('Delete this email rule? This cannot be undone.', { title: 'Delete rule', confirmLabel: 'Delete', cancelLabel: 'Cancel', dangerous: true });
		if (!tmpOk) { return; }
		try
		{
			await this.provider.deleteRuleAsync(tmpRule.IDEmailRule);
			this.modal.toast('Email rule deleted.', { type: 'success' });
			window.location.hash = '#/EmailNotifications';
		}
		catch (pError)
		{
			this.modal.toast(`Delete failed: ${pError && pError.message ? pError.message : pError}`, { type: 'error', duration: 6000 });
		}
	}

	async cloneRule()
	{
		const tmpRule = this.provider.editorState.Rule;
		if (!tmpRule || !tmpRule.IDEmailRule) { return; }
		const tmpOk = await this.modal.confirm('Create a copy of this rule? The copy keeps the same targeting; its custom email content resets to the handler default.', { title: 'Clone rule', confirmLabel: 'Clone', cancelLabel: 'Cancel' });
		if (!tmpOk) { return; }
		try
		{
			const tmpNewID = await this.provider.cloneRuleAsync(tmpRule);
			this.modal.toast('Rule cloned.', { type: 'success' });
			window.location.hash = tmpNewID ? `#/EmailRule/${tmpNewID}` : '#/EmailNotifications';
		}
		catch (pError)
		{
			this.modal.toast(`Clone failed: ${pError && pError.message ? pError.message : pError}`, { type: 'error', duration: 6000 });
		}
	}
}

module.exports = EmailNotificationsEditor;

module.exports.default_configuration =
{
	ViewIdentifier: 'EmailNotificationsEditor',
	DefaultRenderable: 'EmailNotificationsEditorWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.emne-wrap { padding: 1.25rem 1.5rem 2rem; max-width: 46rem; }
		.emne-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1rem; }
		.emne-crumbs { font-size: 0.8rem; margin-bottom: 0.2rem; }
		.emne-crumbs a { color: var(--bulma-text-weak, #7a8598); font-weight: 600; text-decoration: none; }
		.emne-crumbs a:hover { color: var(--bulma-link, #156dd1); }
		.emne-title { font-weight: 700; font-size: 1.35rem; display: flex; align-items: center; gap: 0.5rem; }
		.emne-title .icon { color: var(--bulma-link, #156dd1); }
		.emne-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; justify-content: flex-end; }
		.emne-dirty { display: inline-flex; align-items: center; gap: 0.35rem; color: #b8860b; font-size: 0.78rem; font-weight: 600; }
		.emne-dirty .icon { font-size: 0.5rem; }
		.emne-badge { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.75rem; padding: 0.2rem 0.6rem; border-radius: 999px; background: var(--bulma-scheme-main-bis, #f2f4f8); color: var(--bulma-text-weak, #7a7a7a); border: 1px solid var(--bulma-border, #e6e9ef); }
		.emne-card { border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 12px; background: var(--bulma-scheme-main, #fff); padding: 1.25rem 1.35rem; }
		.emne-field { margin-bottom: 1.1rem; }
		.emne-field:last-child { margin-bottom: 0; }
		.emne-label { display: block; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--bulma-text-weak, #7a8598); margin-bottom: 0.35rem; }
		.emne-help { font-size: 0.75rem; color: var(--bulma-text-weak, #9aa3b2); margin-top: 0.3rem; }
		.emne-handler-desc { font-size: 0.82rem; color: #4a5568; background: var(--bulma-scheme-main-bis, #f6f8fc); border-radius: 8px; padding: 0.5rem 0.7rem; margin-top: 0.4rem; }
		.emne-enable-row { display: flex; align-items: center; gap: 0.5rem; font-weight: 600; color: #22334c; }
		.emne-divider { height: 1px; background: var(--bulma-border, #eef0f4); margin: 1.3rem -1.35rem; }
		.emne-section-title { display: flex; align-items: center; gap: 0.4rem; font-size: 0.9rem; font-weight: 700; color: #22334c; margin-bottom: 0.9rem; }
		.emne-section-title .icon { color: var(--bulma-link, #156dd1); }
		.emne-sched-note { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.72rem; font-weight: 700; color: #b26a2c; background: #fdf3e0; border-radius: 999px; padding: 0.1rem 0.5rem; margin-left: 0.4rem; }
		.emne-days { display: grid; grid-template-columns: repeat(auto-fill, minmax(4rem, 1fr)); gap: 0.3rem 0.6rem; }
		.emne-day { display: flex; align-items: center; gap: 0.35rem; font-size: 0.82rem; color: #2c3e50; }
		.emne-json { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.8rem; }

		/* --- "Applies to" dual-list pickers --- */
		/* Fixed pane height + a flexing list: both panes end on exactly the same line, whatever they hold. */
		.emne-dual { display: grid; grid-template-columns: 1fr auto 1fr; gap: 0.6rem; align-items: stretch; }
		.emne-dualpane { min-width: 0; height: 17.5rem; display: flex; flex-direction: column; }
		.emne-dualhead { display: flex; align-items: center; gap: 0.35rem; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--bulma-text-weak, #7a8598); margin-bottom: 0.3rem; }
		.emne-dualcount { font-weight: 600; color: var(--bulma-link, #156dd1); text-transform: none; letter-spacing: 0; }
		.emne-dualfilter { margin-bottom: 0.3rem; }
		.emne-duallist { border: 1px solid var(--bulma-border, #dbe0e8); border-radius: 8px; background: var(--bulma-scheme-main, #fff); flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 0.2rem; }
		.emne-duallist-selected { background: var(--bulma-scheme-main-bis, #f8fafd); }
		.emne-dualitem { display: flex; align-items: center; gap: 0.35rem; width: 100%; text-align: left; border: none; background: transparent; font-size: 0.8rem; color: #22334c; padding: 0.22rem 0.4rem; border-radius: 5px; cursor: pointer; line-height: 1.25; }
		.emne-dualitem:hover:not([disabled]) { background: #eaf2fd; color: var(--bulma-link, #156dd1); }
		.emne-dualitem[disabled] { cursor: default; opacity: 0.75; }
		.emne-dualitem > span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.emne-dualitem-selected:hover:not([disabled]) { background: #fdeaec; color: #cc2936; }
		.emne-dualx { opacity: 0; font-size: 0.7rem; }
		.emne-dualitem-selected:hover:not([disabled]) .emne-dualx { opacity: 0.8; }
		.emne-dualunknown { color: #b26a2c; font-size: 0.75rem; }
		.emne-dualempty { font-size: 0.75rem; color: var(--bulma-text-weak, #9aa3b2); padding: 0.4rem 0.45rem; margin: 0; }
		.emne-dualmid { display: flex; flex-direction: column; gap: 0.35rem; justify-content: center; }
		.emne-dualmid .button { white-space: nowrap; }
		@media (max-width: 44rem) { .emne-dual { grid-template-columns: 1fr; } .emne-dualpane { height: 14rem; } .emne-dualmid { flex-direction: row; justify-content: flex-start; } }

		/* --- Per-key config fields --- */
		.emne-cfgfield { border: 1px solid var(--bulma-border, #eef0f4); border-radius: 10px; padding: 0.85rem 0.95rem; margin-bottom: 0.85rem; background: var(--bulma-scheme-main, #fff); }
		.emne-cfghead { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.15rem; }
		.emne-cfghead .emne-label { margin-bottom: 0; }
		.emne-cfghead .emne-spacer { flex: 1; }
		.emne-override { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.75rem; font-weight: 600; color: var(--bulma-text-weak, #7a8598); white-space: nowrap; cursor: pointer; }
		.emne-cfgbody { margin-top: 0.55rem; }
		.emne-badge-warn { background: #fdf3e0; color: #b26a2c; border-color: #f0dcb8; }
		.emne-fielderror { color: #cc2936; font-size: 0.75rem; margin-top: 0.25rem; min-height: 0.9rem; }
		.emne-warn { color: #b26a2c; }

		/* --- Default (not-overridden) previews --- */
		.emne-default { font-size: 0.8rem; color: #4a5568; margin: 0; }
		.emne-default-empty { color: var(--bulma-text-weak, #9aa3b2); font-style: italic; }
		.emne-default-pre { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.76rem; background: var(--bulma-scheme-main-bis, #f6f8fc); border-radius: 6px; padding: 0.4rem 0.55rem; white-space: pre-wrap; word-break: break-word; overflow-x: auto; }
		.emne-default-list { margin: 0; padding-left: 1.1rem; list-style: disc; }
		.emne-default-details > summary { cursor: pointer; font-size: 0.78rem; color: var(--bulma-link, #156dd1); }
		.emne-default-details > .emne-default-pre { margin-top: 0.4rem; max-height: 18rem; overflow: auto; }

		/* --- Repeatable rows --- */
		.emne-rows { display: flex; flex-direction: column; gap: 0.4rem; }
		.emne-row { display: flex; align-items: center; gap: 0.4rem; }
		.emne-row .emne-rowvalue { flex: 1; min-width: 0; }
		.emne-row .emne-rowvalue > .select, .emne-row .emne-rowvalue > .select > select { width: 100%; }
		.emne-row .emne-ff, .emne-row .emne-fv, .emne-row .emne-mk, .emne-row .emne-mv { flex: 1; min-width: 0; }
		.emne-row .emne-mv, .emne-row .emne-fv { flex: 2; }
		.emne-rowop { font-size: 0.74rem; color: var(--bulma-text-weak, #9aa3b2); text-transform: uppercase; letter-spacing: 0.04em; }
		.emne-rowdel { border: none; background: transparent; color: var(--bulma-text-weak, #9aa3b2); cursor: pointer; padding: 0.2rem 0.35rem; border-radius: 5px; line-height: 1; }
		.emne-rowdel:hover { color: #cc2936; background: #fdeaec; }
		.emne-rowwarn { color: #b26a2c; font-size: 0.8rem; }
		.emne-rowadd { margin-top: 0.5rem; }

		/* --- Chips --- */
		.emne-chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
		.emne-chips:not(:empty) { margin-bottom: 0.45rem; }
		.emne-chip { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.78rem; padding: 0.15rem 0.3rem 0.15rem 0.55rem; border-radius: 999px; background: var(--bulma-scheme-main-bis, #f2f4f8); border: 1px solid var(--bulma-border, #e6e9ef); color: #22334c; }
		.emne-chip-warn { background: #fdf3e0; border-color: #f0dcb8; color: #b26a2c; }
		.emne-chipdel { border: none; background: transparent; color: inherit; opacity: 0.55; cursor: pointer; padding: 0 0.15rem; line-height: 1; font-size: 0.7rem; }
		.emne-chipdel:hover { opacity: 1; color: #cc2936; }
		.emne-chipadd { display: flex; gap: 0.4rem; align-items: center; }
		.emne-chipadd .input { flex: 1; }

		/* --- Checkbox lists / inline controls --- */
		.emne-checklist { display: grid; grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr)); gap: 0.25rem 0.75rem; max-height: 16rem; overflow-y: auto; }
		.emne-checkfilter { margin-bottom: 0.45rem; }
		.emne-nomatch { font-style: italic; }
		.emne-checkitem { display: flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; color: #2c3e50; }
		.emne-inline { display: flex; gap: 0.4rem; align-items: center; }
		.emne-inline > .input { flex: 1; }

		/* --- Message-body code editors --- */
		.emne-code { border: 1px solid var(--bulma-border, #dbe0e8); border-radius: 8px; overflow: hidden; }
		.emne-code .cm-editor { max-height: 26rem; font-size: 0.8rem; }
		.emne-code .cm-scroller { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.emne-code-fallback { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.8rem; }
		.emne-footer { display: flex; gap: 0.5rem; margin-top: 1.4rem; align-items: center; }
		.emne-footer .emne-spacer { flex: 1; }
		.emne-overlay { padding: 3rem 1rem; text-align: center; color: var(--bulma-text-weak, #7a7a7a); }
		.emne-spinner { display: inline-block; width: 1.8rem; height: 1.8rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #156dd1); border-radius: 50%; animation: emne-spin 0.8s linear infinite; }
		@keyframes emne-spin { to { transform: rotate(360deg); } }
	`,

	Templates:
	[
		{
			Hash: 'EmailNotifications-Editor-Wrap',
			Template: /*html*/`
				<div class="emne-wrap">
					<div class="emne-header">
						<div>
							<div class="emne-crumbs"><a href="#/EmailNotifications"><span class="icon is-small"><i class="fas fa-chevron-left"></i></span> Email Notifications</a></div>
							<div class="emne-title"><span class="icon"><i class="fas fa-envelope-open-text"></i></span> {~D:AppData.EmailNotifications.EditorTitle~}</div>
						</div>
						<div class="emne-actions" id="EmailNotifications-Editor-Actions"></div>
					</div>
					{~TS:EmailNotifications-Editor-Error:AppData.EmailNotifications.EditorErrorSlot~}
					{~TS:EmailNotifications-Editor-Loading:AppData.EmailNotifications.EditorLoadingSlot~}
					{~TS:EmailNotifications-Editor-Form:AppData.EmailNotifications.EditorRuleSlot~}
				</div>`
		},
		{
			Hash: 'EmailNotifications-Editor-Form',
			Template: /*html*/`
				<div class="emne-card">
					<div class="emne-field">
						<label class="emne-enable-row"><input type="checkbox" id="emne-enabled" {~D:Record.Enabled~} {~D:Record.DisabledAttr~} onchange="_Pict.views.EmailNotificationsEditor.onFieldChanged()"> Rule enabled</label>
						<p class="emne-help">When off, this rule sends no email.</p>
					</div>

					<div class="emne-field">
						<label class="emne-label">Email type</label>
						<div class="select is-fullwidth"><select id="emne-handler" {~D:Record.DisabledAttr~} onchange="_Pict.views.EmailNotificationsEditor.onHandlerChanged()">{~TS:EmailNotifications-Editor-HandlerOption:Record.HandlerOptions~}</select></div>
						<div id="EmailNotifications-Handler-Desc"></div>
					</div>

					<div class="emne-divider"></div>
					<div class="emne-section-title"><span class="icon is-small"><i class="fas fa-filter"></i></span> Applies to</div>

					<div class="emne-field">
						<label class="emne-label">Projects</label>
						<div id="EmailNotifications-Projects"></div>
						<p class="emne-help">Select none = <strong>all projects</strong>.</p>
					</div>

					<div class="emne-field">
						<label class="emne-label">Document type</label>
						<div class="select is-fullwidth"><select id="emne-doctype" {~D:Record.DisabledAttr~} onchange="_Pict.views.EmailNotificationsEditor.onFieldChanged()">{~TS:EmailNotifications-Editor-DocTypeOption:Record.DocTypeOptions~}</select></div>
					</div>

					<div class="emne-field">
						<label class="emne-label">Status</label>
						<div id="EmailNotifications-Status"></div>
						<p class="emne-help">Select none = <strong>any status</strong>.</p>
					</div>

					<div id="EmailNotifications-Dynamic"></div>

					<div class="emne-footer">
						{~TS:EmailNotifications-Editor-DeleteClone:Record.ExistingSlot~}
						<span class="emne-spacer"></span>
						{~TS:EmailNotifications-Editor-ReadOnlyNote:Record.ReadOnlySlot~}
					</div>
				</div>`
		},
		{
			Hash: 'EmailNotifications-Editor-HandlerOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>`
		},
		{
			Hash: 'EmailNotifications-Editor-DocTypeOption',
			Template: /*html*/`<option value="{~D:Record.Hash~}" {~D:Record.SelectedAttr~}>{~D:Record.Name~}</option>`
		},
		{
			Hash: 'EmailNotifications-Editor-DeleteClone',
			Template: /*html*/`
				<button class="button is-danger is-light" onclick="_Pict.views.EmailNotificationsEditor.deleteRule()"><span class="icon is-small"><i class="fas fa-trash"></i></span><span>Delete</span></button>
				<button class="button is-light" onclick="_Pict.views.EmailNotificationsEditor.cloneRule()"><span class="icon is-small"><i class="fas fa-clone"></i></span><span>Clone</span></button>`
		},
		{
			Hash: 'EmailNotifications-Editor-ReadOnlyNote',
			Template: /*html*/`<span class="emne-badge"><span class="icon is-small"><i class="fas fa-eye"></i></span> Read only</span>`
		},
		{
			Hash: 'EmailNotifications-Editor-Actions-Template',
			Template: /*html*/`
				{~TS:EmailNotifications-Editor-Actions-ReadOnly:Record.ReadOnlySlot~}
				{~TS:EmailNotifications-Editor-Actions-Dirty:Record.DirtySlot~}
				{~TS:EmailNotifications-Editor-Actions-Buttons:Record.EditSlot~}`
		},
		{
			Hash: 'EmailNotifications-Editor-Actions-ReadOnly',
			Template: /*html*/`<span class="emne-badge" title="Editing requires customer-admin permission."><span class="icon is-small"><i class="fas fa-eye"></i></span> Read only</span>`
		},
		{
			Hash: 'EmailNotifications-Editor-Actions-Dirty',
			Template: /*html*/`<span class="emne-dirty"><span class="icon is-small"><i class="fas fa-circle"></i></span> Unsaved changes</span>`
		},
		{
			Hash: 'EmailNotifications-Editor-Actions-Buttons',
			Template: /*html*/`
				<button class="button is-small is-light" onclick="_Pict.views.EmailNotificationsEditor.cancel()">Cancel</button>
				<button class="button is-small is-success" onclick="_Pict.views.EmailNotificationsEditor.saveRule()"><span class="icon is-small"><i class="fas fa-floppy-disk"></i></span><span>Save rule</span></button>`
		},
		{
			Hash: 'EmailNotifications-Editor-Loading',
			Template: /*html*/`<div class="emne-overlay"><span class="emne-spinner"></span><p class="mt-3">Loading…</p></div>`
		},
		{
			Hash: 'EmailNotifications-Editor-Error',
			Template: /*html*/`<div class="notification is-danger is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Message~}</span></div>`
		}
	],

	Renderables:
	[
		{ RenderableHash: 'EmailNotificationsEditorWrap', TemplateHash: 'EmailNotifications-Editor-Wrap', ContentDestinationAddress: '#PlatformConfiguration-Application-Container' }
	]
};
